import {useEffect, useRef} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {EnrollStepsProvider, useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {
  isProductStep,
  PROTECTION_BLURB,
  STEP,
  STEP_LABELS,
  stepProduct,
} from '@client/pages/public/enroll/enrollModel';
import type {EnrollStep} from '@client/pages/public/enroll/enrollModel';
import {groupRequiresAccessCode, type GroupConfig} from '@client/pages/public/enroll/types';
import {ProgressTracker} from '@client/pages/public/enroll/components/ProgressTracker';
import {StepNav} from '@client/pages/public/enroll/components/StepNav';
import {CartRail} from '@client/pages/public/enroll/components/CartRail';
import {getEnrollAccess} from '@client/pages/public/enroll/enrollAccess';

function stepHeader(
  group: GroupConfig,
  currentStep: EnrollStep | undefined,
): {title: string; description?: string} | null {
  if (!currentStep) return null;
  // Product steps are 1 product → 1 step, so the header comes straight from the product config.
  if (isProductStep(currentStep)) {
    const product = stepProduct(group, currentStep);
    return product ? {title: product.label, description: product.description} : null;
  }
  // Smell: the protection step aggregates N products, so there's no single product to read a
  // header from — it falls back to shared defaults. If a group ever needs custom copy,
  // consider a section in the group config instead of special-casing it here.
  if (currentStep === STEP.Protection) {
    return {title: STEP_LABELS[STEP.Protection], description: PROTECTION_BLURB};
  }
  // Review/Checkout/Confirmation render their own headers inside their step components.
  return null;
}

function EnrollStepsLayout() {
  const {group, currentStep, currentIndex} = useEnrollSteps();
  const {track} = useEnrollAnalytics();
  const showCart = Boolean(
    currentStep && (isProductStep(currentStep) || currentStep === STEP.Protection),
  );
  // Checkout renders its own footer (Back + form submit) so the submit can trigger RHF validation.
  const showStepNav = currentStep !== STEP.Confirmation && currentStep !== STEP.Checkout;
  // Confirmation is a terminal receipt — drop the progress bar entirely.
  const showProgress = currentStep !== STEP.Confirmation;
  const header = stepHeader(group, currentStep);

  const prevIndexRef = useRef(currentIndex);
  const goingBack = currentIndex < prevIndexRef.current;
  useEffect(() => {
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (!currentStep) return;
    track(EnrollEvent.StepViewed, {step: currentStep});
  }, [currentStep, track]);

  return (
    <div>
      {showProgress && (
        <div className='-mt-10 border-b border-neutral-200 bg-white [margin-left:calc(50%-50vw)] [margin-right:calc(50%-50vw)]'>
          <div className='mx-auto max-w-7xl px-6 py-4'>
            <ProgressTracker />
          </div>
        </div>
      )}

      <div className={cn('space-y-8', showProgress && 'mt-8')}>
        <div
          key={currentStep}
          className={cn(
            'space-y-8 duration-500 animate-in fade-in',
            goingBack ? 'slide-in-from-left-6' : 'slide-in-from-right-6',
          )}
        >
          {header && (
            <div className={cn('space-y-2', POSTHOG_UNMASK_CLASS)}>
              <h1 className='text-3xl font-bold text-[var(--enroll-ink)]'>{header.title}</h1>
              {header.description && (
                <p className='max-w-2xl text-neutral-600'>{header.description}</p>
              )}
            </div>
          )}

          <div className={showCart ? 'grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]' : undefined}>
            <div className='min-w-0'>
              <Outlet />
            </div>
            {showCart && (
              <div>
                <div className='lg:sticky lg:top-6'>
                  <CartRail />
                </div>
              </div>
            )}
          </div>
        </div>

        {showStepNav && <StepNav />}
      </div>
    </div>
  );
}

export function EnrollSteps() {
  const group = useEnrollGroup();

  // Gate: code-based groups require a validated access code -> redirect to the access-code screen.
  // Public groups (requiresAccessCode: false) skip the gate entirely.
  if (groupRequiresAccessCode(group) && !getEnrollAccess(group.slug)) {
    return <Navigate to={`/${group.slug}/access-code`} replace />;
  }

  return (
    <EnrollStepsProvider key={group.slug}>
      <EnrollStepsLayout />
    </EnrollStepsProvider>
  );
}
